import { call, selectPresentationNumber, useDispatch, useSelector } from '@missionlabs/api'
import { Menu, MenuItem, MenuList, useFormatToNumberE164 } from '@missionlabs/react'
import { SMSIcon, WhatsappIcon } from '@missionlabs/react/circleloop'
import {
    ContactType,
    DirectoryEntry,
    OutboundMessageType,
    WhatsAppContentType
} from '@missionlabs/types'
import { DrawerMode } from 'features/voice/ContactDrawer'
import { FC, ReactNode, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ContactNumberMenuItem } from 'shared/components/ContactNumberMenuItem'
import { useCanSendWhatsApp } from 'shared/hooks/useCanSendWhatsApp'
import { useGetMyNumbers } from 'shared/hooks/useGetMyNumbers'
import { useIsSMSEnabled } from 'shared/hooks/useIsSMSEnabled'
import { addMessage } from 'shared/slices/messagingSlice'

import { useContactDrawerState } from '../../voice/hooks/useContactDrawerState'

interface ContactNumberOverflowProps {
    contact: DirectoryEntry
    trigger: ReactNode
    variant?: 'dark'
    contactType?: ContactType
    includeMessaging?: boolean
}

export const ContactNumberOverflow: FC<ContactNumberOverflowProps> = ({
    contact,
    trigger,
    variant,
    contactType,
    includeMessaging
}) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { setMode } = useContactDrawerState()

    const { phoneNumbers = [] } = contact

    const { isUKMobileNumber, formatToNumberE164 } = useFormatToNumberE164()

    const mobileNumbers = useGetMyNumbers({ mobileOnly: true })
    const presentationNumber = useSelector(selectPresentationNumber) ?? ''

    const handleCall = useCallback(
        (numberOrExt: string) => {
            dispatch(call({ to: numberOrExt, from: presentationNumber, contact }))
            setMode(DrawerMode.VOICE)
        },
        [dispatch, presentationNumber, contact, setMode]
    )

    const contactMobileNumbers = useMemo(() => {
        return phoneNumbers.filter(({ numberE164 }) => isUKMobileNumber(numberE164))
    }, [phoneNumbers, isUKMobileNumber])

    const isSMSEnabled = useIsSMSEnabled(contactMobileNumbers.map(number => number.numberE164))
    const isWhatsAppEnabled = useCanSendWhatsApp()

    const handleSMS = useCallback(
        (numberOrExt: string) => {
            dispatch(
                addMessage({
                    type: OutboundMessageType.SMS,
                    contact,
                    toNumber: numberOrExt,
                    fromNumber: mobileNumbers[0].value,
                    text: ''
                })
            )
        },
        [dispatch, contact, mobileNumbers]
    )

    const handleWhatsApp = (toNumber: string) => {
        dispatch(
            addMessage({
                type: OutboundMessageType.WhatsApp,
                contentType: WhatsAppContentType.TEXT,
                contact,
                toNumber,
                numberID: '',
                text: ''
            })
        )
    }

    return (
        <Menu
            variant={variant}
            onOpen={() => {
                if (contactType === 'internal' && contact.extension) {
                    handleCall(contact.extension)
                }
            }}
        >
            {trigger}
            {(contactType !== 'internal' || !contact.extension) && (
                <MenuList>
                    {contact.extension && contactType !== 'internal' && (
                        <MenuItem variant={variant} onClick={() => handleCall(contact.extension!)}>
                            {t('Internal')}
                        </MenuItem>
                    )}
                    {contact.phoneNumbers?.map(number => (
                        <ContactNumberMenuItem
                            key={number.numberE164}
                            number={number}
                            onClick={numberOrExt => handleCall(formatToNumberE164(numberOrExt))}
                            variant={variant}
                        />
                    ))}
                    {isSMSEnabled && includeMessaging && (
                        <>
                            {contactMobileNumbers.map(option => {
                                return (
                                    <MenuItem
                                        name="SMS"
                                        key={`sms-${option.numberE164}`}
                                        icon={<SMSIcon />}
                                        variant={variant}
                                        onClick={() => handleSMS(option.numberE164)}
                                    >
                                        {option.numberE164}
                                    </MenuItem>
                                )
                            })}
                        </>
                    )}
                    {isWhatsAppEnabled && includeMessaging && (
                        <>
                            {contactMobileNumbers.map(option => {
                                return (
                                    <MenuItem
                                        name="WhatsApp"
                                        key={`whatsapp-${option.numberE164}`}
                                        icon={<WhatsappIcon boxSize="22px" />}
                                        variant={variant}
                                        onClick={() => handleWhatsApp(option.numberE164)}
                                    >
                                        {option.numberE164}
                                    </MenuItem>
                                )
                            })}
                        </>
                    )}
                </MenuList>
            )}
        </Menu>
    )
}
